// What the shell's own re-reads key on (#1811): a count of the writes this tab
// has made, and one read in flight per key for the reads a site's pages share.
//
// **A write is the operator's own act.** Every request the client sends that
// is not a read, answered 2xx, and not marked `Zenith-Unattended` moves the
// count, so a save, an import, a bind or an approval is seen by the shell
// whatever page made it, and no page has to remember to say so. A plan, a
// preview or a `:read` is a POST that writes nothing, so it moves nothing: the
// work list it would re-read is the one already on screen. Another operator's
// write is not seen here, and the shell's next read after a navigation is
// where it shows (no change stream is held for it: ADR-0240 decision 2a).
//
// **One read in flight per key, never a stack of them** (#1811). The work
// list's read plans every managed switch at the cell, and a site's page and
// the shell each ask for it when the page opens, so the second asker joins the
// first rather than sending its own. A read joins only one started since the
// last write, because a read started before a save cannot show the save. There
// is no cache: a settled read is forgotten, and the next asker reads again.
import { useSyncExternalStore } from "react";
import type { Middleware } from "openapi-fetch";
import { within } from "./read";

let epoch = 0;
const listeners = new Set<() => void>();

/** How many writes this tab has made. Read it to know whether a read is
 * older than the last write. */
export function writeEpoch(): number {
  return epoch;
}

/** Counts one write and tells whoever listens. */
export function wrote(): void {
  epoch++;
  for (const l of listeners) l();
}

/** The write count, as a value a component re-renders on. */
export function useWriteEpoch(): number {
  return useSyncExternalStore((l) => { listeners.add(l); return () => { listeners.delete(l); }; }, writeEpoch);
}

/** Whether a request is the operator's own write: not a read, not one of the
 * POSTs that only read (a plan, a preview, a `:read`), and not marked as
 * asked by nobody. */
export function isWrite(method: string, url: string, unattended: string | null): boolean {
  const m = method.toUpperCase();
  if (m === "GET" || m === "HEAD" || m === "OPTIONS") return false;
  if (unattended === "true") return false;
  const path = url.split("?")[0] ?? "";
  return !/:(plan|preview|read)$/.test(path);
}

export const countWrites: Middleware = {
  async onResponse({ request, response }) {
    if (response.ok && isWrite(request.method, request.url, request.headers.get("Zenith-Unattended"))) wrote();
    return response;
  },
};

const inFlight = new Map<string, { epoch: number; pending: Promise<unknown> }>();

/** The read in flight under `key` if one started since the last write, or a
 * new one from `start`. It is forgotten when it settles.
 *
 * `start` runs as no `useQuery` run's read (`read.ts`): the promise is handed
 * to every asker under `key`, and the run that happened to start it must not
 * stop it by leaving while another asker still waits on it (#1861). */
export function shared<T>(key: string, start: () => Promise<T>): Promise<T> {
  const have = inFlight.get(key);
  if (have && have.epoch === epoch) return have.pending as Promise<T>;
  const pending = within(undefined, start);
  const entry = { epoch, pending };
  inFlight.set(key, entry);
  const forget = () => { if (inFlight.get(key) === entry) inFlight.delete(key); };
  pending.then(forget, forget);
  return pending;
}

/** Forgets every read in flight, for a test that must not join the read the
 * test before it left out. */
export function forgetInFlight(): void {
  inFlight.clear();
}
